const APP_RELOAD_KEY = 'gaia:app-initiated-reload';

/**
 * How soon after `reloadApp()` the next document must start for the marker to count.
 * Guards against a stale marker when the reloaded document never consumed it (e.g. a
 * logout reload that bounced straight to the login page), which would otherwise hide a
 * later genuine refresh in the same tab.
 */
const MARKER_MAX_AGE_MS = 10_000;

/**
 * Reload the document on the app's behalf (e.g. a logout path).
 *
 * Navigation Timing reports `'reload'` for `window.location.reload()` exactly as it
 * does for a user refresh, so this leaves a per-tab marker that `useTrackPageRefresh`
 * consumes to avoid counting app-initiated reloads as `Page Refreshed`.
 *
 * Direct `location.reload()` calls elsewhere are banned by `no-restricted-syntax`
 * in eslint.config.mjs.
 */
export function reloadApp() {
  try {
    sessionStorage.setItem(APP_RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — the reload may be counted as a user refresh.
  }
  window.location.reload();
}

/**
 * Returns true if the current document was loaded by `reloadApp()`.
 * Always clears the marker, so it applies at most once.
 */
export function consumeAppReloadMarker(): boolean {
  try {
    const markedAt = Number(sessionStorage.getItem(APP_RELOAD_KEY));
    sessionStorage.removeItem(APP_RELOAD_KEY);
    if (!markedAt) return false;

    // The reloaded document starts (timeOrigin) just after the marker was written.
    const elapsed = performance.timeOrigin - markedAt;
    return elapsed >= 0 && elapsed <= MARKER_MAX_AGE_MS;
  } catch {
    return false;
  }
}
